import { rem } from 'polished'
import { useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { unique } from 'lib/array/arrayUtils'
import { inclusionTravelPeriodFilter } from 'lib/offer/inclusionsTravelPeriodFilter'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const ServiceItem = styled.div`
  padding: ${rem(16)} 0;
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`

interface Props {
  offer: App.VillaOffer
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number
}

function VillasVipServices({ offer, checkIn, checkOut, duration }: Props) {
  const vipServices = useMemo(() => {
    const packages = offer.packages.filter(pkg => pkg.duration === duration)
    const allInclusions = packages.flatMap(pkg => pkg.inclusions ?? [])
    const filteredInclusions = (checkIn && checkOut) ? inclusionTravelPeriodFilter(allInclusions, checkIn, checkOut) : allInclusions

    const allServices = unique(filteredInclusions.map(({ description }) => description))
    return allServices.map(description => {
      if (description.startsWith('-')) {
        return description.slice(1).trim()
      }
      return description
    })
  }, [offer.packages, duration, checkIn, checkOut])

  if (vipServices.length === 0) return null
  return <VerticalSpacer gap={8}>
    <Heading variant="heading4">
      <FormattedMessage
        id="opKY31TbFFkljH2LiLWq"
        defaultMessage="Enjoy your stay with our <exclusive>VIP services</exclusive>"
        description="VIP services section heading on villa offer page on the offer detail page"
        values={{
          exclusive: (chunks: React.ReactNode) => <Heading variant="heading4" as="span" colour="exclusive">{chunks}</Heading>,
        }}
      />
    </Heading>
    <ServicesGrid>
      {vipServices.map((vipService) => <ServiceItem key={vipService}>
        <SolidCheckCircleIcon colour="exclusive" />
        <BodyText variant="medium" weight="bold" colour="neutral-two">{vipService}</BodyText>
      </ServiceItem>)}
      <LoyaltyInclusions offer={offer}>
        {(loyaltyItems) => loyaltyItems.map((item) => <ServiceItem key={item.id}>
          <SolidCheckCircleIcon colour="exclusive" />
          <BodyText variant="medium" weight="bold" colour="neutral-two">{item.description}</BodyText>
        </ServiceItem>)}
      </LoyaltyInclusions>
    </ServicesGrid>
  </VerticalSpacer>
}

export default withOfferPageState((state) => ({
  offer: state.offer as App.VillaOffer,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
}), VillasVipServices)
